import { useEffect, useState } from "react";
import { api, ROLE_LABELS, formatDate } from "@/lib/api";
import { IdCard, Upload, Download, Award, FileCheck, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Membership({ user, isAdmin, canViewDocs, refetch }) {
  const [members, setMembers] = useState([]);
  const [files, setFiles] = useState([]);
  const [phone, setPhone] = useState(user.phone || "");
  const [codiceFiscale, setCodiceFiscale] = useState(user.codice_fiscale || "");
  const [address, setAddress] = useState(user.address || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [idDoc, setIdDoc] = useState(null); // {has_document, file_id, filename}
  const [uploadingId, setUploadingId] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (isAdmin || canViewDocs) {
      const r = await api.get("/users"); setMembers(r.data);
    }
    const f = await api.get("/files/mine"); setFiles(f.data);
    try { const d = await api.get("/me/document"); setIdDoc(d.data); } catch { setIdDoc({ has_document: false }); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadIdDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Documento troppo grande (max 5 MB)"); e.target.value = ""; return; }
    setUploadingId(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/me/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento identità caricato — sarà verificato dal Tesseramento");
      window.dispatchEvent(new CustomEvent("giacomo:say", { detail: { text: "Documento caricato — sei quasi ufficiale! 🎉" } }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally { setUploadingId(false); e.target.value = ""; }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "document");
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato"); load();
      window.dispatchEvent(new CustomEvent("giacomo:say", { detail: { text: "Un passo avanti verso la tessera! 📎" } }));
    } catch { toast.error("Errore upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const savePhone = async () => {
    await api.patch(`/users/${user.user_id}/phone`, { phone });
    toast.success("Telefono salvato"); refetch && refetch();
  };

  const saveProfile = async () => {
    const cf = codiceFiscale.trim().toUpperCase();
    if (cf && !/^[A-Z0-9]{16}$/.test(cf)) {
      toast.error("Codice Fiscale non valido (16 caratteri alfanumerici)"); return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/me/profile", { codice_fiscale: cf, address: address.trim() });
      toast.success("Dati fiscali salvati"); refetch && refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSavingProfile(false); }
  };

  const issueTessera = async (uid, custom = null) => {
    try {
      const r = await api.post(`/tessera/issue/${uid}`, custom ? { tessera_number: custom } : {});
      toast.success(`Tessera ${r.data.tessera_number} emessa`); load();
      if (uid === user.user_id) refetch && refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore emissione tessera"); }
  };

  const assignNumber = (uid) => {
    const v = window.prompt("Numero tessera da assegnare (es. 13 oppure AMN-0013).\nLascia vuoto per assegnazione automatica:");
    if (v === null) return;
    issueTessera(uid, v.trim() || null);
  };

  const openTessera = (uid) => {
    window.open(`${backend}/api/tessera/${uid}/pdf`, "_blank");
  };

  return (
    <div data-testid="section-membership">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><IdCard className="w-3 h-3" /> Tesseramenti</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">La tua Tessera Socio</h1>
        <p className="text-[#6B6558] mt-2">Documenti, dati e tessera digitale con QR code — pronta in un click.</p>
      </div>

      {/* Banner: ID document upload (per utenti Google-login senza doc) */}
      {idDoc && !idDoc.has_document && (
        <div
          data-testid="id-doc-upload-banner"
          className="paper rounded-2xl p-5 mb-6 border-l-4 border-[#EF3E36]"
          style={{ background: "linear-gradient(180deg, #FDF2F2, #FFFFFF)" }}
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#EF3E36] text-white flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold text-[#2B3A33]">Carica il tuo documento di riconoscimento</div>
              <p className="text-sm text-[#6B6558] mt-1">
                Sei entrato con Google ma non hai ancora caricato un documento d'identità. Serve al Tesseramento per completare la verifica. Formati accettati: PDF, JPG, PNG, WEBP (max 5 MB).
              </p>
              <div className="mt-3">
                <label className="terracotta-btn px-4 py-2 rounded-lg font-semibold inline-flex items-center gap-2 cursor-pointer text-sm">
                  <Upload className="w-4 h-4" />
                  {uploadingId ? "Caricamento..." : "Carica documento"}
                  <input
                    type="file"
                    data-testid="upload-id-doc-input"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
                    className="hidden"
                    onChange={uploadIdDoc}
                    disabled={uploadingId}
                  />
                </label>
              </div>
              <p className="text-xs text-[#8A8272] mt-2">
                🔒 Il documento è visibile solo al Consiglio, Tesoriere, Segretario e Tesseramento (Reg. UE 2016/679).
              </p>
            </div>
          </div>
        </div>
      )}
      {idDoc && idDoc.has_document && (
        <div
          data-testid="id-doc-uploaded-banner"
          className="paper rounded-2xl p-4 mb-6 border-l-4 border-[#16A34A] bg-[#F0FDF4] flex items-center gap-3"
        >
          <ShieldCheck className="w-5 h-5 text-[#16A34A] flex-shrink-0" />
          <div className="text-sm text-[#065F46] flex-1">
            <strong>Documento identità caricato</strong> — <span className="text-[#3C4A44]">{idDoc.filename}</span>. Il Tesseramento lo verificherà a breve.
          </div>
          <label className="text-xs px-3 py-1.5 rounded-lg border border-[#16A34A] text-[#065F46] font-semibold hover:bg-white cursor-pointer">
            Sostituisci
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf" className="hidden" onChange={uploadIdDoc} disabled={uploadingId} />
          </label>
        </div>
      )}

      {/* Personal card */}
      <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <div className="paper rounded-2xl p-6" data-testid="my-tessera">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">La mia tessera</div>
              <div className="font-display text-2xl font-bold text-[#2B3A33] mt-1 break-words">{user.name}</div>
              <div className="text-sm text-[#6B6558] break-all">{user.email}</div>
              <span className={`role-${user.role} text-[10px] px-2 py-0.5 rounded-full inline-block font-semibold uppercase tracking-wide mt-2`}>
                {ROLE_LABELS[user.role]}
              </span>
            </div>
            {user.picture && <img src={user.picture} alt="" className="w-16 h-16 rounded-xl" />}
          </div>
          <div className="mt-5 border-t border-dashed border-[#E8E2D8] pt-4">
            {user.tessera_number ? (
              <div>
                <div className="flex items-center gap-2 text-sm">
                  <Award className="w-4 h-4 text-[#D97706]" />
                  <span className="font-mono font-bold text-[#2B3A33]">{user.tessera_number}</span>
                </div>
                <div className="text-xs text-[#6B6558] mt-1">
                  Emessa {formatDate(user.tessera_issued_at)} · Scadenza {formatDate(user.tessera_expires_at)}
                </div>
                <button
                  data-testid="download-my-tessera"
                  onClick={() => openTessera(user.user_id)}
                  className="mt-4 terracotta-btn px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Scarica PDF tessera
                </button>
              </div>
            ) : (
              <div className="text-sm text-[#6B6558]">
                La tua tessera non è ancora stata emessa. Attendi che il Consiglio la rilasci.
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-[#E8E2D8] pt-4">
            <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><Phone className="w-3 h-3" /> Numero WhatsApp</label>
            <div className="flex gap-2 mt-2">
              <input data-testid="phone-input" placeholder="+39…" value={phone} onChange={e => setPhone(e.target.value)} className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[#E8E2D8]" />
              <button data-testid="save-phone" onClick={savePhone} className="terracotta-btn px-3 py-2 rounded-lg text-sm">Salva</button>
            </div>
          </div>

          <div className="mt-5 border-t border-[#E8E2D8] pt-4">
            <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Dati fiscali per Ricevuta</label>
            <p className="text-[10px] text-[#8A8272] mt-1">Necessari per emettere la ricevuta della tua quota o donazione.</p>
            <div className="mt-2 space-y-2">
              <input
                data-testid="cf-input"
                placeholder="Codice Fiscale (16 caratteri)"
                value={codiceFiscale}
                onChange={e => setCodiceFiscale(e.target.value.toUpperCase())}
                maxLength={16}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8] font-mono uppercase tracking-wider"
              />
              <input
                data-testid="address-input"
                placeholder="Indirizzo di residenza (opzionale)"
                value={address}
                onChange={e => setAddress(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D8]"
              />
              <button
                data-testid="save-profile"
                onClick={saveProfile}
                disabled={savingProfile}
                className="terracotta-btn px-3 py-2 rounded-lg text-sm disabled:opacity-60"
              >
                {savingProfile ? "Salvataggio…" : "Salva dati fiscali"}
              </button>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="paper rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">I miei documenti</div>
              <div className="font-display text-xl font-bold text-[#2B3A33]">Carica i documenti richiesti</div>
            </div>
            <label data-testid="upload-doc-label" className="terracotta-btn px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer inline-flex items-center gap-2">
              <Upload className="w-4 h-4" /> {uploading ? "Carico…" : "Carica"}
              <input data-testid="upload-doc-input" type="file" className="hidden" onChange={uploadFile} />
            </label>
          </div>
          <div className="space-y-2">
            {files.length === 0 && (
              <div className="text-sm text-[#6B6558] py-4 text-center">Nessun documento caricato. Carica Carta d&apos;Identità e Codice Fiscale.</div>
            )}
            {files.map(f => (
              <div key={f.id} data-testid={`file-${f.id}`} className="flex items-center justify-between bg-white border border-[#E8E2D8] rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileCheck className="w-4 h-4 text-[#16A34A] shrink-0" />
                  <span className="text-sm text-[#2B3A33] truncate">{f.original_filename}</span>
                </div>
                <a href={`${backend}/api/files/${f.id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[#EF3E36] font-semibold hover:underline">Apri</a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Admin: member directory */}
      {isAdmin && (
        <div className="paper rounded-2xl p-6 mt-6" data-testid="member-directory">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Addetti Tesseramenti</div>
              <div className="font-display text-xl font-bold text-[#2B3A33]">Elenco Soci</div>
            </div>
            <div className="text-sm text-[#6B6558]">{members.length} iscritti</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
                  <th className="py-2">Nome</th>
                  <th className="py-2 hidden sm:table-cell">Email</th>
                  <th className="py-2">Ruolo</th>
                  <th className="py-2">Tessera</th>
                  <th className="py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.user_id} className="border-b border-[#E8E2D8]/60" data-testid={`member-${m.user_id}`}>
                    <td className="py-3 font-medium text-[#2B3A33]">{m.name}</td>
                    <td className="py-3 text-[#6B6558] hidden sm:table-cell">{m.email}</td>
                    <td className="py-3"><span className={`role-${m.role} text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase`}>{ROLE_LABELS[m.role]}</span></td>
                    <td className="py-3 font-mono text-xs">{m.tessera_number || "—"}</td>
                    <td className="py-3 text-right">
                      <button data-testid={`issue-${m.user_id}`} onClick={() => issueTessera(m.user_id)} className="text-xs px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] mr-1">
                        {m.tessera_number ? "Rinnova" : "Emetti"}
                      </button>
                      <button data-testid={`assign-num-${m.user_id}`} onClick={() => assignNumber(m.user_id)} title="Assegna numero manualmente" className="text-xs px-2 py-1 rounded border border-[#E8E2D8] hover:bg-[#FDF2F2] mr-1">N°</button>
                      {m.tessera_number && (
                        <button data-testid={`open-tessera-${m.user_id}`} onClick={() => openTessera(m.user_id)} className="text-xs px-2 py-1 rounded terracotta-btn">PDF</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
